import { Droplet, Zap, Award, Flame, ArrowRight, MessageSquare, ShieldCheck } from "lucide-react";
import { useState, useEffect } from "react";
import { websiteConfig } from "@/data/website-config";

export default function Hero() {
  const { hero_images } = websiteConfig;
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [headlineIndex, setHeadlineIndex] = useState(0);
  const [textFade, setTextFade] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);

    const imagesToRotate = hero_images || websiteConfig.settings.hero_images;
    if (imagesToRotate && imagesToRotate.length > 1) {
      const imageInterval = setInterval(() => {
        setCurrentImageIndex((prevIndex) => (prevIndex + 1) % imagesToRotate.length);
      }, 4500);

      return () => clearInterval(imageInterval);
    }
  }, [hero_images]);

  // 2-Second Headline Rotating Switcher
  useEffect(() => {
    const headlineInterval = setInterval(() => {
      setTextFade(false);
      setTimeout(() => {
        setHeadlineIndex((prev) => (prev === 0 ? 1 : 0));
        setTextFade(true);
      }, 200); // 200ms smooth fade transition
    }, 2000); // 2 seconds per state

    return () => clearInterval(headlineInterval);
  }, []);

  const images = hero_images || websiteConfig.settings.hero_images;

  return (
    <section id="home" className="relative min-h-[90vh] flex items-center justify-center overflow-hidden pt-16">
      {/* Background Slides */}
      {images.map((image, index) => (
        <div
          key={index}
          className={`bg-transition ${index === currentImageIndex ? 'active' : 'inactive'}`}
          style={{
            backgroundImage: `url('${image}')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          role="img"
          aria-label={`SunAura Slide ${index + 1}`}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/90 via-slate-900/80 to-slate-950/95" />
        </div>
      ))}

      {/* Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-16">
        <div className="space-y-6">
          {/* Badge */}
          <div className={`${isLoaded ? 'slide-down' : 'opacity-0'}`}>
            <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-red-600/20 text-red-400 border border-red-500/30 text-xs font-bold tracking-wide backdrop-blur-md">
              <Award className="w-3.5 h-3.5 text-red-400" />
              <span>Authorized Racold Distributor • Ranchi, Jharkhand</span>
            </div>
          </div>

          {/* Animated 2-Second Rotating Headline Container */}
          <div className="min-h-[140px] sm:min-h-[170px] lg:min-h-[190px] flex items-center justify-center">
            <div
              className={`transition-all duration-300 transform ${
                textFade ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-95 -translate-y-2"
              }`}
            >
              {headlineIndex === 0 ? (
                <h1 className="text-4xl sm:text-6xl lg:text-7xl font-black text-white tracking-tight leading-tight">
                  Smarter Water Heating <br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 via-orange-400 to-amber-400">
                    For Every Home & Project
                  </span>
                </h1>
              ) : (
                <h1 className="text-6xl sm:text-8xl lg:text-9xl font-black tracking-tight leading-none text-transparent bg-clip-text bg-gradient-to-r from-white via-red-100 to-amber-300 drop-shadow-2xl">
                  SunAura
                </h1>
              )}
            </div>
          </div>

          {/* Short, Punchy Subtitle */}
          <p className={`text-base sm:text-lg text-slate-300 max-w-2xl mx-auto font-normal ${isLoaded ? 'slide-up stagger-1' : 'opacity-0'}`}>
            Genuine Racold Storage Geysers, Instant Heaters, Central Heat Pumps & Solar Systems with same-day Ranchi delivery.
          </p>

          {/* Clean Feature Badges */}
          <div className={`flex flex-wrap justify-center gap-2 sm:gap-3 pt-2 ${isLoaded ? 'slide-up stagger-2' : 'opacity-0'}`}>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/10 backdrop-blur-md border border-white/10 text-white text-xs font-medium">
              <Flame className="w-3.5 h-3.5 text-red-400" /> Storage (10L-100L)
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/10 backdrop-blur-md border border-white/10 text-white text-xs font-medium">
              <Zap className="w-3.5 h-3.5 text-amber-400" /> Instant Tankless
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/10 backdrop-blur-md border border-white/10 text-white text-xs font-medium">
              <ShieldCheck className="w-3.5 h-3.5 text-blue-400" /> Heat Pumps (70% Saver)
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/10 backdrop-blur-md border border-white/10 text-white text-xs font-medium">
              <Droplet className="w-3.5 h-3.5 text-emerald-400" /> Solar Heating
            </span>
          </div>

          {/* Action CTAs */}
          <div className={`flex flex-wrap items-center justify-center gap-3 pt-4 ${isLoaded ? 'scale-in stagger-3' : 'opacity-0'}`}>
            <button
              onClick={() => document.getElementById('products').scrollIntoView({ behavior: 'smooth' })}
              className="px-6 py-3.5 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl shadow-lg shadow-red-600/30 transition-all flex items-center gap-2 text-sm hover:-translate-y-0.5"
            >
              <span>Explore Products</span>
              <ArrowRight className="w-4 h-4" />
            </button>

            <button
              onClick={() => document.getElementById('sizing-calculator').scrollIntoView({ behavior: 'smooth' })}
              className="px-5 py-3.5 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-xl backdrop-blur-md border border-white/20 transition-all text-sm hover:-translate-y-0.5"
            >
              Capacity Calculator
            </button>

            <a
              href={`https://wa.me/${websiteConfig.settings.whatsapp_number}?text=${encodeURIComponent('Hello SunAura! I would like to inquire about Racold water heaters.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-5 py-3.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl shadow-md shadow-emerald-600/30 transition-all flex items-center gap-2 text-sm hover:-translate-y-0.5"
            >
              <MessageSquare className="w-4 h-4" />
              <span>WhatsApp Us</span>
            </a>
          </div>
        </div>
      </div>

      {/* Bottom Wave */}
      <div className="absolute bottom-0 left-0 right-0 z-10 pointer-events-none">
        <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
          <path d="M0 30L60 25C120 20 240 10 360 15C480 20 600 35 720 40C840 45 960 40 1080 30C1200 20 1320 10 1380 5L1440 0V60H1380C1320 60 1200 60 1080 60C960 60 840 60 720 60C600 60 480 60 360 60C240 60 120 60 60 60H0V30Z" fill="#f8fafc" />
        </svg>
      </div>
    </section>
  );
}
